import {
  getFontCss,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { Choose2faMethodSuccessToastMessage } from 'src/app/Toast/Toast'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingPopupContent from 'src/components/LoadingPopupContent/LoadingPopupContent'
import Popup from 'src/components/Popup/Popup'
import RadioItems from 'src/components/RadioItems/RadioItems'
import Tooltip from 'src/components/Tooltip/Tooltip'
import TopLevelError from 'src/components/TopLevelError/TopLevelError'
import { MfaPreferences } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { usePatchUser, useQueryOwnUser } from 'src/queries/users.queries'
import { createSetIsChoose2faMethodPopupOpen } from 'src/redux/actions/app.actions'
import { formatPhoneNumber } from 'src/utils/string.utils'
import { css, CSSProp } from 'styled-components'

const RadioButtonStyling = (isSelected: boolean): CSSProp => {
  return css`
    ${getFontCss(
      16,
      isSelected
        ? themeConstants.colors.cta
        : themeConstants.colors.font_primary,
      styleConstants.fontFamilies.medium,
    )};
    display: flex;
    flex-direction: column;
    gap: 4px;
  `
}

const Choose2faMethodPopup = (): React.ReactElement => {
  const dispatch = useDispatch()
  const translate = useTranslate()

  const { data: user, isPending: isUserPending } = useQueryOwnUser({})

  const { mutate: save2faMethod, isPending: isLoading, error } = usePatchUser()

  const [method, setMethod] = useState<MfaPreferences>()
  useEffect(() => {
    if (!isUserPending && user !== undefined && method === undefined) {
      // default to email if mfaPreference is noMfa
      const current =
        user?.mfaPreference === MfaPreferences.noMfa
          ? MfaPreferences.email
          : user?.mfaPreference
      setMethod(current)
    }
  }, [user, isUserPending, method])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    save2faMethod(
      { body: { mfaPreference: method } },
      {
        onSuccess: (): void => {
          // close popup and create toast
          dispatch(createSetIsChoose2faMethodPopupOpen(false))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <Choose2faMethodSuccessToastMessage />,
            }),
          )
        },
      },
    )
  }

  return (
    <Popup
      onClose={(): void => {
        dispatch(createSetIsChoose2faMethodPopupOpen(false))
      }}
    >
      {isUserPending ? (
        <LoadingPopupContent />
      ) : (
        <form
          onSubmit={onSubmit}
          css={css`
            display: flex;
            flex-direction: column;
            gap: 40px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 20px;
            `}
          >
            <div
              css={css`
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('Choose default method for one-time codes')}
            </div>
            <div
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate(
                "Choose how you'd like to receive your one-time verification codes.",
              )}
            </div>
            <div
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate(
                'By continuing, you agree that we can send you the code using the method you select. Message and data rates may apply.',
              )}
            </div>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <RadioItems
              gap="8px"
              items={[
                <div
                  key={0}
                  css={RadioButtonStyling(method === MfaPreferences.email)}
                >
                  {user?.email}
                  <div
                    css={css`
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    {translate('Email')}
                  </div>
                </div>,
                <div
                  key={1}
                  css={RadioButtonStyling(method === MfaPreferences.sms)}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      gap: 8px;
                      align-items: center;
                    `}
                  >
                    <span
                      css={css`
                        ${!user?.phoneVerified &&
                        css`
                          opacity: 50%;
                        `}
                        ${!user?.phone &&
                        css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      `}
                    >
                      {user?.phone
                        ? formatPhoneNumber(user.phone)
                        : translate('No phone number available')}
                    </span>
                    {!user?.phoneVerified && (
                      <Tooltip
                        iconSrc={
                          user?.phone ? (
                            <Tag
                              text={
                                <span
                                  css={css`
                                    ${getFontCss(
                                      12,
                                      themeConstants.colors.font_primary,
                                      styleConstants.fontFamilies.medium,
                                    )};
                                  `}
                                >
                                  {translate('Unverified')}
                                </span>
                              }
                              size={TagSizes.Small}
                            />
                          ) : undefined
                        }
                        openLeft={false}
                      >
                        {translate(
                          user?.phone
                            ? "To choose this option, you'll need to finish verifying this phone number."
                            : "To choose this option, you'll need to add your phone number.",
                        )}
                        <span
                          css={css`
                            margin-top: 16px;
                          `}
                        >
                          {translate(
                            'You can do so in Profile Settings > Personal Info.',
                          )}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <div
                    css={css`
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                      ${!user?.phoneVerified &&
                      css`
                        opacity: 50%;
                      `}
                    `}
                  >
                    {translate('Mobile')}
                  </div>
                </div>,
              ]}
              isItemDisabled={(index: number): boolean => {
                return index === 1 && !user?.phoneVerified
              }}
              setIndex={(index: number): void =>
                setMethod(
                  index === 0 ? MfaPreferences.email : MfaPreferences.sms,
                )
              }
              currentIndex={method === MfaPreferences.email ? 0 : 1}
              whiteBackground
              ariaLabels={[translate('Email'), translate('Mobile')]}
            />
            {error?.error && <TopLevelError error={error?.error} />}
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              gap: 20px;
            `}
          >
            <Button
              variant="primary"
              type="submit"
              loading={isLoading}
              testId={'sumbitBtn'}
            >
              {isLoading ? 'Saving...' : translate('Save Preference')}
            </Button>
            <HoverableLink
              ariaLabelSource={'Cancel'}
              onClick={(): void => {
                dispatch(createSetIsChoose2faMethodPopupOpen(false))
              }}
              testId={'cancelBtn'}
            >
              {translate('Cancel')}
            </HoverableLink>
          </div>
        </form>
      )}
    </Popup>
  )
}

export default Choose2faMethodPopup
